import React, { useEffect, useRef, useState } from 'react';
import { FeedbackRating } from '../utils/api';

interface FeedbackModalProps {
  rating: FeedbackRating;
  initialComment: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (comment: string) => void;
  onClose: () => void;
}

/**
 * Comment dialog opened by the thumbs up/down buttons. The rating save is
 * already in flight when this opens — the modal only collects an optional
 * written comment, and Submit re-sends the full rating+comment upsert (which
 * also retries a failed rating save), so closing it (Escape / overlay /
 * Cancel) loses nothing beyond an unsubmitted comment.
 */
const FeedbackModal: React.FC<FeedbackModalProps> = ({
  rating,
  initialComment,
  submitting,
  error,
  onSubmit,
  onClose,
}) => {
  const [comment, setComment] = useState(initialComment);
  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Remember what was focused (the thumb button) so focus can be restored
    // there when the modal closes.
    const opener = document.activeElement as HTMLElement | null;
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Trap Tab inside the modal so keyboard focus can't reach the chat behind it.
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // Prevent the chat behind the modal from scrolling while it's open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [onClose]);

  const title = rating === 'up' ? 'What did you like?' : 'What could be improved?';

  return (
    <div
      className="feedback-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={modalRef} className="feedback-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="feedback-modal-header">
          <span className="feedback-modal-title">{title}</span>
          <button
            className="feedback-modal-close"
            onClick={onClose}
            aria-label="Close feedback dialog"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <textarea
          ref={textareaRef}
          className="feedback-modal-textarea"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add more details (optional)…"
          maxLength={2000}
          rows={4}
        />
        {error && (
          <p className="feedback-modal-error" role="alert">{error}</p>
        )}
        <div className="feedback-modal-actions">
          <button className="feedback-modal-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="feedback-modal-submit"
            onClick={() => onSubmit(comment)}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
